import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  createServiceCatalog,
  deleteServiceCatalog,
  fetchServiceCatalog,
  updateServiceCatalog,
} from "../services/maintenanceService";
import type { MaintenanceServiceRate, ServiceCatalogItem } from "../types";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { useMxLabels } from "../hooks/useMxLabels";
import { fetchServiceRates } from "../services/maintenanceService";
import { CATEGORY_ICONS, type Category } from "../theme";
import { fmtMoney } from "../lib";
import { PageShell, ViewSwitch } from "../components/kit/Primitives";
import { Photo } from "../components/kit/Media";
import { Badge, EmptyState } from "../../../components/ui";

import { DIALOG_COLORS } from "../../../components/ui";
// Service catalog for the external workshop: the priced services that feed the
// POS charge. This IS the POS products catalog (issue #79) — every item is a POS
// product of type "service", so prices, taxes and administration are shared with
// the POS. CRUD goes through the /pos/product endpoints.
export default function CatalogoSection() {
  const { t } = useTranslation("common");
  const L = useMxLabels();
  const [tab, setTab] = useState<"pos" | "rates">("rates");
  const [rates, setRates] = useState<MaintenanceServiceRate[]>([]);
  const [ratesLoading, setRatesLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchServiceRates()
      .then((r) => alive && setRates(r))
      .catch(() => alive && setRates([]))
      .finally(() => alive && setRatesLoading(false));
    return () => {
      alive = false;
    };
  }, []);
  const { capabilities } = useMaintenanceCapabilities();
  const [rows, setRows] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchServiceCatalog());
    } catch {
      setError(t("mantenimiento.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const fields: FormField[] = useMemo(
    () => [
      {
        key: "name",
        label: t("mantenimiento.fields.serviceName"),
        type: "text",
        required: true,
      },
      {
        key: "price",
        label: t("mantenimiento.fields.price"),
        type: "number",
        required: true,
        defaultValue: 0,
      },
      {
        key: "taxRate",
        label: t("mantenimiento.fields.taxRate"),
        type: "number",
        defaultValue: 16,
      },
      {
        key: "description",
        label: t("mantenimiento.fields.description"),
        type: "textarea",
      },
    ],
    [t]
  );

  const columns: Column[] = useMemo(
    () => [
      { key: "name", label: t("mantenimiento.fields.serviceName") },
      {
        key: "description",
        label: t("mantenimiento.fields.description"),
        render: (r) => String(r.description ?? "—"),
      },
      {
        key: "taxRate",
        label: t("mantenimiento.fields.taxRate"),
        align: "center",
        render: (r) => `${Number(r.taxRate ?? 16)}%`,
      },
      {
        key: "price",
        label: t("mantenimiento.fields.price"),
        align: "right",
        render: (r) =>
          `$${Number(r.price || 0).toLocaleString("es-MX", {
            minimumFractionDigits: 2,
          })}`,
      },
    ],
    [t]
  );

  const handleCreate = async (values: Record<string, string>) => {
    await createServiceCatalog({
      name: values.name,
      price: Number(values.price || 0),
      taxRate: Number(values.taxRate || 16),
      description: values.description || null,
    });
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updateServiceCatalog(id, {
      name: values.name,
      price: Number(values.price || 0),
      taxRate: Number(values.taxRate || 16),
      description: values.description || null,
    });
    await load();
  };

  const handleDelete = async (id: string) => {
    const res = await Swal.fire({
      title: t("mantenimiento.confirmDeleteTitle"),
      text: t("mantenimiento.confirmDeleteText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("mantenimiento.delete"),
      cancelButtonText: t("mantenimiento.cancel"),
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    await deleteServiceCatalog(id);
    await load();
  };

  return (
    <PageShell
      icon="ri-price-tag-3-line"
      title={t("mantenimiento.menu.catalogo")}
      subtitle={tab === "pos" ? t("mantenimiento.catalogoSubtitle") : t("mantenimiento.ui.catalogo.tabuladorHint")}
      toolbar={
        <ViewSwitch
          value={tab}
          onChange={setTab}
          options={[
            { id: "rates", icon: "ri-timer-line", label: `${t("mantenimiento.ui.catalogo.tabulador")} · ${rates.length}` },
            { id: "pos", icon: "ri-shopping-bag-3-line", label: `${t("mantenimiento.ui.catalogo.pos")} · ${rows.length}` },
          ]}
        />
      }
    >
      {tab === "pos" ? (
        <ApiCrudSection
          embedded
          title={t("mantenimiento.menu.catalogo")}
          columns={columns}
          fields={fields}
          rows={rows as unknown as Row[]}
          loading={loading}
          error={error}
          readOnly={!capabilities.canManageCatalog}
          searchKey="name"
          addLabel={t("mantenimiento.newService")}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      ) : rates.length === 0 && !ratesLoading ? (
        <div className="rounded-xl border border-dashed border-line bg-card">
          <EmptyState icon="ri-timer-line" title={t("mantenimiento.ui.catalogo.empty")} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {rates.map((r) => {
            const cat = (r.category || "otro") as Category;
            return (
              <article key={r.id} className="flex gap-3 overflow-hidden rounded-xl border border-line bg-card p-3">
                <Photo src={r.photo} alt={r.name} thumb fallbackIcon={CATEGORY_ICONS[cat] ?? "ri-tools-line"} className="h-20 w-24 shrink-0 rounded-lg" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="line-clamp-2 text-sm font-medium text-content">{r.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <Badge tone="neutral" size="sm">
                      <i className={CATEGORY_ICONS[cat] ?? "ri-tools-line"} />
                      {L.category(cat)}
                    </Badge>
                    {r.sku ? <span className="font-mono text-[10px] text-content-muted">{r.sku}</span> : null}
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-1 text-xs">
                    <span className="text-content-secondary"><i className="ri-timer-line mr-1" />{t("mantenimiento.ui.catalogo.minutes", { count: r.durationMinutes })}</span>
                    <span className="font-semibold tabular-nums text-content">{fmtMoney(r.price, L.locale)}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
